import { useState, useEffect, useRef } from "react";
import NavigationButtons from "./NavigationButtons.jsx";
import { generateInterviewEmail } from "../utils/emailGenerators.jsx";
import { useAutoSave } from "../hooks/useAutoSave.js";
import SavedStatusIndicator from "./SavedStatusIndicator.jsx";
import InterviewPreview from "./InterviewPreview.jsx";
import RestorePrompt from "./RestorePrompt.jsx";

const TOTAL_STEPS = 5;

const INITIAL_FORM_DATA = {
  studentName: "",
  studentYear: "Select Year",
  studentTitle: "",
  studentPronouns: "",
  sourceName: "",
  sourceTitle: "",
  addContext: false,
  articleTopic: "",
  focusPoints: "",
  sourceConnection: "",
  interviewLength: "Select Length",
  availability: "",
};

function validateStep(step, data, tags) {
  switch (step) {
    case 1:
      if (!data.studentName.trim()) return { field: "studentName", message: "Please enter your name." };
      if (!data.studentYear || data.studentYear === "Select Year") return { field: "studentYear", message: "Please select your year." };
      break;
    case 2:
      if (!data.sourceName.trim()) return { field: "sourceName", message: "Please enter the source's name." };
      if (!data.sourceTitle.trim()) return { field: "sourceTitle", message: "Please enter the source's title." };
      break;
    case 3:
      if (!data.articleTopic.trim()) return { field: "articleTopic", message: "Please enter the article topic." };
      if (tags.length === 0) return { field: "focusPoints", message: "Please add at least one focus point." };
      break;
    case 4:
      if (!data.sourceConnection.trim()) return { field: "sourceConnection", message: "Please explain why this source is relevant." };
      if (!data.interviewLength || data.interviewLength === "Select Length") return { field: "interviewLength", message: "Please select an interview length." };
      break;
    case 5:
      if (!data.availability.trim()) return { field: "availability", message: "Please enter your availability." };
      break;
    default:
      break;
  }
  return null;
}

/**
 * InterviewForm component - A multi-step form for generating interview request emails
 */
function InterviewForm({ onGenerateEmail, onStartOver, startFresh }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPreview, setShowPreview] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [restoreDismissed, setRestoreDismissed] = useState(false);

  const {
    data: formData,
    setData: setFormData,
    clearSavedData,
    restoreSavedData,
    hasSavedData,
    isSaving,
    lastSaved,
    saveError,
  } = useAutoSave("pawPrint_interview_formData", INITIAL_FORM_DATA);

  // Tags state (derived from and synced to formData.focusPoints)
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");
  const tagsInitialized = useRef(false);

  // Initialize tags from saved focusPoints once
  useEffect(() => {
    if (tagsInitialized.current) return;
    if (formData.focusPoints) {
      const savedTags = formData.focusPoints
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
      if (savedTags.length > 0) {
        setTags(savedTags);
      }
    }
    tagsInitialized.current = true;
  }, [formData.focusPoints]);

  // Handle startFresh prop
  useEffect(() => {
    if (startFresh) {
      setFormData(INITIAL_FORM_DATA);
      setTags([]);
      setCurrentStep(1);
      setValidationError(null);
      tagsInitialized.current = true;
    }
  }, [startFresh, setFormData]);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
    if (validationError?.field === id) {
      setValidationError(null);
    }
  };

  const handleAddTag = () => {
    const trimmed = currentTag.trim();
    if (!trimmed) return;
    if (tags.includes(trimmed)) return;

    const newTags = [...tags, trimmed];
    setTags(newTags);
    setFormData((prev) => ({
      ...prev,
      focusPoints: newTags.join(", "),
    }));
    setCurrentTag("");
    if (validationError?.field === "focusPoints") {
      setValidationError(null);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(newTags);
    setFormData((prev) => ({
      ...prev,
      focusPoints: newTags.join(", "),
    }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleNext = () => {
    const error = validateStep(currentStep, formData, tags);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const handlePrev = () => {
    setValidationError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleGenerate = () => {
    const error = validateStep(currentStep, formData, tags);
    if (error) {
      setValidationError(error);
      return;
    }
    const { subject, body } = generateInterviewEmail(formData);
    clearSavedData();
    setTags([]);
    tagsInitialized.current = false;
    setRestoreDismissed(true);
    onGenerateEmail(subject, body);
  };

  const stepClass = (step) =>
    `form-step ${currentStep === step ? "active-step" : ""}`;

  return (
    <form
      id="interview-form"
      className="email-form"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="progress-indicator" id="interview-progress">
        <div className="step-dots">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`step-dot ${
                i + 1 === currentStep
                  ? "active"
                  : i + 1 < currentStep
                  ? "completed"
                  : ""
              }`}
            />
          ))}
        </div>
        Step {currentStep} of {TOTAL_STEPS}
      </div>

      {hasSavedData && !restoreDismissed && (
        <RestorePrompt
          lastSaved={lastSaved}
          onResume={() => {
            restoreSavedData();
            setRestoreDismissed(true);
          }}
          onStartFresh={() => {
            clearSavedData();
            setRestoreDismissed(true);
          }}
        />
      )}

      <SavedStatusIndicator
        isSaving={isSaving}
        lastSaved={lastSaved}
        saveError={saveError}
        onClearSavedData={clearSavedData}
        formType="interview"
      />

      <h2>
        <i className="fas fa-microphone-alt"></i> Draft Interview Request
      </h2>

      <button type="button" className="start-over-btn" onClick={onStartOver}>
        <i className="fas fa-redo"></i> Start Over
      </button>

      {/* Step 1 */}
      <div className={stepClass(1)} data-step="1">
        <h3>Step 1: Your Information</h3>

        <label htmlFor="studentName">Your Name:</label>
        <input
          type="text"
          id="studentName"
          value={formData.studentName}
          onChange={handleChange}
          aria-invalid={validationError?.field === "studentName"}
          aria-describedby={validationError?.field === "studentName" ? "interview-error" : undefined}
        />

        <label htmlFor="studentYear">Your Year:</label>
        <select
          id="studentYear"
          value={formData.studentYear}
          onChange={handleChange}
          aria-invalid={validationError?.field === "studentYear"}
        >
          <option value="">Select Year</option>
          <option value="freshman">Freshman</option>
          <option value="sophomore">Sophomore</option>
          <option value="junior">Junior</option>
          <option value="senior">Senior</option>
        </select>

        <label htmlFor="studentTitle">
          Your Title (Optional, e.g., Staff Writer):
        </label>
        <input
          type="text"
          id="studentTitle"
          value={formData.studentTitle}
          onChange={handleChange}
          placeholder="e.g., Beat Editor, Staff Writer"
        />

        <label htmlFor="studentPronouns">Your Pronouns (Optional):</label>
        <input
          type="text"
          id="studentPronouns"
          value={formData.studentPronouns}
          onChange={handleChange}
          placeholder="e.g., she/her, they/them"
        />
      </div>

      {/* Step 2 */}
      <div className={stepClass(2)} data-step="2">
        <h3>Step 2: Source Information</h3>

        <label htmlFor="sourceName">
          Source&apos;s Name (e.g., Mr. Smith, Noah Garcia):
        </label>
        <input
          type="text"
          id="sourceName"
          value={formData.sourceName}
          onChange={handleChange}
          aria-invalid={validationError?.field === "sourceName"}
        />

        <label htmlFor="sourceTitle">
          Source&apos;s Role/Title (e.g., Principal, English Teacher):
        </label>
        <input
          type="text"
          id="sourceTitle"
          value={formData.sourceTitle}
          onChange={handleChange}
          aria-invalid={validationError?.field === "sourceTitle"}
        />

        <label htmlFor="addContext" style={{ display: 'inline-block', marginBottom: 'calc(var(--spacing-medium) + 10px)', fontWeight: 'normal' }}>
          <input
            type="checkbox"
            id="addContext"
            checked={formData.addContext}
            onChange={handleChange}
          />
          Add extra context about Paw Print (for non-WHS staff/students)?
        </label>
      </div>

      {/* Step 3 */}
      <div className={stepClass(3)} data-step="3">
        <h3>Step 3: Article Details</h3>

        <label htmlFor="articleTopic">Article Topic:</label>
        <input
          type="text"
          id="articleTopic"
          value={formData.articleTopic}
          onChange={handleChange}
          placeholder="e.g., New Lunch Menu, Chess Club Success"
          aria-invalid={validationError?.field === "articleTopic"}
        />

        <label htmlFor="focusPoints">Main focus/points of the article:</label>
        <div className="tags-input-container">
          <div className="tags-display">
            {tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
                <button
                  type="button"
                  className="tag-remove-btn"
                  onClick={() => handleRemoveTag(tag)}
                  aria-label={`Remove ${tag} tag`}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
          <div className="tag-input-wrapper">
            <input
              type="text"
              id="currentTag"
              value={currentTag}
              onChange={(e) => setCurrentTag(e.target.value)}
              onKeyDown={handleTagKeyDown}
              placeholder="Type a tag and press Enter or click Add"
              className="tag-input"
              aria-invalid={validationError?.field === "focusPoints"}
            />
            <button
              type="button"
              className="add-tag-btn"
              onClick={handleAddTag}
              disabled={!currentTag.trim() || tags.includes(currentTag.trim())}
            >
              Add
            </button>
          </div>
          <input type="hidden" id="focusPoints" value={formData.focusPoints} />
          <p className="info-text">
            <i className="fas fa-info-circle"></i>
            Add individual tags for the main focus points of your article. Click
            on any tag to remove it.
          </p>
        </div>
      </div>

      {/* Step 4 */}
      <div className={stepClass(4)} data-step="4">
        <h3>Step 4: Relevance &amp; Length</h3>

        <label htmlFor="sourceConnection">
          Why this source is relevant to the topic:
        </label>
        <textarea
          id="sourceConnection"
          rows="3"
          value={formData.sourceConnection}
          onChange={handleChange}
          placeholder="e.g., you oversee the cafeteria changes, you advise the relevant club, your class curriculum covers this topic"
          aria-invalid={validationError?.field === "sourceConnection"}
        ></textarea>

        <label htmlFor="interviewLength">Suggested Interview Length:</label>
        <select
          id="interviewLength"
          value={formData.interviewLength}
          onChange={handleChange}
          aria-invalid={validationError?.field === "interviewLength"}
        >
          <option value="">Select Length</option>
          <option value="1-5 minute">1-5 minutes</option>
          <option value="5-10 minute">5-10 minutes</option>
          <option value="10-15 minute">10-15 minutes</option>
          <option value="15-20 minute">15-20 minutes</option>
          <option value="20-30 minute">20-30 minutes</option>
          <option value="longer than 30 minute">30+ minutes</option>
        </select>
      </div>

      {/* Step 5 */}
      <div className={stepClass(5)} data-step="5">
        <h3>Step 5: Scheduling &amp; Review</h3>

        <label htmlFor="availability">
          Your General Availability (Be specific):
        </label>
        <textarea
          id="availability"
          rows="3"
          value={formData.availability}
          onChange={handleChange}
          placeholder="e.g., during tutorial on A/B days, lunch on Monday/Wednesday, after school until 4 PM on Tuesdays"
          aria-invalid={validationError?.field === "availability"}
        ></textarea>

        <p>Ready to generate the email?</p>
      </div>

      {validationError && (
        <div id="interview-error" className="validation-error" role="alert">
          <i className="fas fa-exclamation-circle"></i> {validationError.message}
        </div>
      )}

      {showPreview && (
        <InterviewPreview formData={formData} currentStep={currentStep} />
      )}

      <div className="toggle-switch-container">
        <label className="toggle-switch-label">
          <input
            type="checkbox"
            className="toggle-switch-input"
            checked={showPreview}
            onChange={() => setShowPreview((prev) => !prev)}
            role="switch"
            aria-label="Toggle email preview"
          />
          <span className="toggle-switch-slider"></span>
        </label>
        <span>Toggle Preview</span>
      </div>

      <NavigationButtons
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        onPrev={handlePrev}
        onNext={handleNext}
        onGenerate={handleGenerate}
        formType="interview"
      />
    </form>
  );
}

export default InterviewForm;
